import React, { useState, useMemo } from 'react';
import {
  ShoppingBag, Zap, Hammer, Cog, Fence, Leaf, Camera, Phone, Globe, Search, X,
} from 'lucide-react';
import { useFacility } from '../context/FacilityContext';
import seed from '../data/marketplaceSeed';

/**
 * Marketplace. A simple directory of local service providers the
 * operator can call when something breaks at the facility. Data lives
 * in src/data/marketplaceSeed.js so an operator can edit by
 * hand without touching the page.
 *
 * Phase-1 surface: 6 category tiles + filterable vendor list. No
 * backend. When/if we promote this to a real listing platform with
 * operator-submitted vendors, ratings, and dispatch, the page swaps
 * the seed import for an API call but keeps the same layout.
 */

const CATEGORIES = [
  { key: 'Electricians', icon: Zap },
  { key: 'Handymen', icon: Hammer },
  { key: 'Gate Technicians', icon: Cog },
  { key: 'Fence Companies', icon: Fence },
  { key: 'Mowing / Landscape', icon: Leaf },
  { key: 'Camera Technicians', icon: Camera },
];

const Marketplace = () => {
  const { currentFacility } = useFacility();
  const [activeCategory, setActiveCategory] = useState(null); // null = All
  const [search, setSearch] = useState('');
  const [cityFilter, setCityFilter] = useState('all');

  // Cities present in seed — populates the city dropdown so it stays in
  // sync with whatever data the operator drops in.
  const allCities = useMemo(() => {
    const set = new Set();
    seed.forEach((v) => v.cities.forEach((c) => set.add(c)));
    return Array.from(set).sort();
  }, []);

  // Counts per category (full seed, ignoring search/city filters — the
  // tile count should reflect what's available in that category overall,
  // not what's currently visible).
  const countsByCategory = useMemo(() => {
    const counts = {};
    CATEGORIES.forEach((c) => { counts[c.key] = 0; });
    seed.forEach((v) => {
      if (counts[v.category] != null) counts[v.category] += 1;
    });
    return counts;
  }, []);

  // Filter pipeline: category → city → search.
  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return seed.filter((v) => {
      if (activeCategory && v.category !== activeCategory) return false;
      if (cityFilter !== 'all' && !v.cities.includes(cityFilter)) return false;
      if (needle && !v.name.toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [activeCategory, cityFilter, search]);

  const clearAll = () => {
    setActiveCategory(null);
    setSearch('');
    setCityFilter('all');
  };

  const hasAnyFilter = activeCategory || search || cityFilter !== 'all';

  return (
    <div>
      {/* Header */}
      <div className="mb-5">
        <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
          <ShoppingBag className="h-5 w-5 text-slate-400" />
          Marketplace
        </h1>
        <p className="text-[13px] text-slate-400 mt-0.5">
          Find local service providers for your facility.
          {currentFacility?.name && <> &middot; {currentFacility.name}</>}
        </p>
      </div>

      {/* Category tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
        {CATEGORIES.map((cat) => {
          const Icon = cat.icon;
          const active = activeCategory === cat.key;
          const count = countsByCategory[cat.key] || 0;
          return (
            <button
              key={cat.key}
              type="button"
              onClick={() => setActiveCategory(active ? null : cat.key)}
              className={`rounded-xl border p-4 text-left transition-colors ${
                active
                  ? 'border-blue-600 bg-blue-50'
                  : 'border-slate-200 bg-white hover:border-slate-300'
              }`}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`flex-shrink-0 h-9 w-9 rounded-lg flex items-center justify-center ${
                    active ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-600'
                  }`}
                >
                  <Icon className="h-4.5 w-4.5" />
                </div>
                <div className="min-w-0">
                  <p className="text-[14px] font-semibold text-slate-900">
                    {cat.key}
                  </p>
                  <p className="text-[12px] text-slate-500 mt-0.5">
                    {count} {count === 1 ? 'vendor' : 'vendors'}
                  </p>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Filter row */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search vendors by name..."
            className="w-full pl-8 pr-3 py-1.5 rounded-md border border-slate-200 bg-white text-[13px] focus:outline-none"
          />
        </div>
        <select
          value={cityFilter}
          onChange={(e) => setCityFilter(e.target.value)}
          aria-label="Filter by city"
          className="rounded-md border border-slate-200 bg-white text-[13px] px-3 py-1.5 focus:outline-none"
        >
          <option value="all">All cities</option>
          {allCities.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        {hasAnyFilter && (
          <button
            type="button"
            onClick={clearAll}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-slate-200 bg-white text-[12px] text-slate-600 hover:bg-slate-50"
          >
            <X className="h-3 w-3" /> Clear filters
          </button>
        )}
        <div className="ml-auto text-[12px] text-slate-500">
          {filtered.length} {filtered.length === 1 ? 'result' : 'results'}
        </div>
      </div>

      {/* Vendor list */}
      {filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center">
          <p className="text-[13px] text-slate-500">
            No vendors match the current filters.
          </p>
          {hasAnyFilter && (
            <button
              type="button"
              onClick={clearAll}
              className="mt-3 text-[12px] text-blue-600 hover:underline"
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map((v) => (
            <div
              key={`${v.name}-${v.phone}`}
              className="rounded-xl border border-slate-200 bg-white p-4"
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0">
                  <p className="text-[14px] font-semibold text-slate-900 truncate">
                    {v.name}
                  </p>
                  <span className="inline-block mt-1 text-[11px] font-medium text-blue-600 bg-blue-50 rounded px-1.5 py-0.5">
                    {v.category}
                  </span>
                </div>
              </div>

              {v.cities && v.cities.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-3">
                  {v.cities.map((c) => (
                    <span
                      key={c}
                      className="text-[11px] text-slate-600 bg-slate-100 rounded px-1.5 py-0.5"
                    >
                      {c}
                    </span>
                  ))}
                </div>
              )}

              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px]">
                <a
                  href={`tel:${v.phone.replace(/[^0-9+]/g, '')}`}
                  className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                >
                  <Phone className="h-3 w-3" /> {v.phone}
                </a>
                {v.website && (
                  <a
                    href={v.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                  >
                    <Globe className="h-3 w-3" /> Website
                  </a>
                )}
              </div>

              {v.notes && (
                <p className="text-[12px] text-slate-500 mt-2 leading-relaxed">
                  {v.notes}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Marketplace;
